"use client"

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getSubscription, subscribeToPlan, getSubscriptionPlans } from '../../services/subscriptionService';
import { getPaymentMethods } from '../../services/paymentService';
import { FiShield, FiCheck, FiStar, FiClock, FiAlertCircle, FiCheckCircle, FiInfo, FiX, FiCreditCard, FiPlus, FiCalendar } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import { socketService } from '../../utils/socketService';
import { formatPlanPrice, formatPlanPricePerMonth } from '../../utils/formatPlanPrice';

// --- COMPOSANTS DE L'UI ---

const SubscriptionStatus = ({ subscription, onUpgrade, onViewDetails }) => {
  const [renewalType, setRenewalType] = useState(subscription?.renewal_type || 'automatic');
  
  if (!subscription) return null;
  
  const toggleRenewalType = () => {
    const newRenewalType = renewalType === 'automatic' ? 'manual' : 'automatic';
    setRenewalType(newRenewalType);
    // You can add an API call here to save the preference
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const endDate = subscription.end_date || subscription.ends_at;
  const now = new Date();
  const daysRemaining = endDate ? Math.max(0, Math.round((new Date(endDate) - now) / (1000 * 60 * 60 * 24))) : 0;
  const isExpired = endDate ? (new Date(endDate) < now) : false;
  
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gray-800/50 border border-amber-500/30 rounded-2xl p-8 shadow-2xl backdrop-blur-sm"
    >
      <div className="flex flex-col md:flex-row items-center gap-8">
        <div className="flex-shrink-0 w-24 h-24 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-full flex items-center justify-center border-4 border-gray-800">
          <FiStar className="w-12 h-12 text-gray-900" />
        </div>
        <div className="flex-1 text-center md:text-left">
          <p className="text-amber-400 font-semibold">Votre forfait actuel</p>
          <h2 className="text-3xl font-bold text-white mt-1 capitalize">
            {subscription.subscription_type || 'Inconnu'}
          </h2>
          <div className={`mt-3 flex items-center justify-center md:justify-start gap-2 ${isExpired ? 'text-red-400' : 'text-green-400'}`}>
            {isExpired ? <FiAlertCircle/> : <FiCheckCircle/>}
            <span className="font-medium">{isExpired ? 'Expiré' : 'Actif'}</span>
          </div>
          <p className="text-gray-400 mt-2 text-sm">
            {isExpired 
              ? `A expiré le ${formatDate(endDate)}.` 
              : `Expire dans ${daysRemaining} ${daysRemaining > 1 ? 'jours' : 'jour'}.`
            }
          </p>
          
          {/* Renewal Type Toggle */}
          <div className="mt-4 pt-4 border-t border-gray-600">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-white">Type de renouvellement</h4>
                <p className="text-xs text-gray-400">
                  {renewalType === 'automatic' 
                    ? 'Renouvellement automatique' 
                    : 'Renouvellement manuel'}
                </p>
              </div>
              <button 
                type="button"
                onClick={toggleRenewalType}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${renewalType === 'automatic' ? 'bg-amber-600' : 'bg-gray-600'}`}
              >
                <span className="sr-only">Changer le type de renouvellement</span>
                <span 
                  className={`${renewalType === 'automatic' ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                />
              </button>
            </div>
            <p className="mt-2 text-xs text-gray-400">
              {renewalType === 'automatic' 
                ? 'Votre abonnement sera renouvelé automatiquement chaque mois.'
                : 'Vous devrez renouveler manuellement votre abonnement chaque mois.'}
            </p>
          </div>
        </div>
        <div className="flex-shrink-0 flex flex-col sm:flex-row gap-4 mt-4 md:mt-0">
          <button onClick={onViewDetails} className="px-6 py-3 bg-gray-700/50 border border-gray-600 text-white font-bold rounded-xl hover:bg-gray-700 transition-colors flex items-center justify-center gap-2">
            <FiInfo className="w-5 h-5"/>Voir les détails
          </button>
          <button onClick={onUpgrade} className="px-6 py-3 bg-gradient-to-r from-amber-500 to-yellow-500 text-gray-900 font-bold rounded-xl hover:opacity-90 transition-opacity">
            Changer de forfait
          </button>
        </div>
      </div>
    </motion.div>
  );
};

const SubscriptionCard = ({ plan, isCurrent, onSubscribe, loading }) => {
  console.log('Rendering SubscriptionCard with plan:', plan);
  const features = Array.isArray(plan.features) ? plan.features : [];
  const price = typeof plan.price === 'number' ? Number(plan.price) : 0;
  console.log('Plan features:', features);
  console.log('Plan price:', price);
  
  return (
    <motion.div
      whileHover={{ y: -5, boxShadow: '0 10px 25px rgba(0,0,0,0.3)' }}
      className={`relative h-full flex flex-col p-8 rounded-2xl border transition-all duration-300 ${isCurrent ? 'bg-amber-500/10 border-amber-500/50' : 'bg-gray-800 border-gray-700 hover:border-gray-600'}`}
    >
      {plan.isPopular && <div className="absolute top-0 right-8 -mt-3 px-3 py-1 bg-amber-500 text-gray-900 text-xs font-bold rounded-full z-10">POPULAIRE</div>}
      <h3 className="text-xl font-semibold text-white">{plan.name || 'Sans titre'}</h3>
      <p className="text-gray-400 mt-2 h-10">{plan.description || 'Aucune description disponible'}</p>
      <div className="mt-6">
        <span className="text-4xl font-bold text-white">
          {price > 0 ? formatPlanPrice(plan.price, plan.currency) : 'Gratuit'}
        </span>
        {price > 0 && (
          <span className="text-gray-400 ml-2">
            /{plan.billing_cycle === 'yearly' ? 'an' : 'mois'}
          </span>
        )}
      </div>
      <ul className="mt-8 space-y-4 text-gray-300 flex-grow">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start">
            <FiCheck className="w-5 h-5 text-green-400 mr-3 flex-shrink-0 mt-1" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
      <div className="mt-8">
        <button onClick={() => onSubscribe(plan)} disabled={loading || isCurrent} className={`w-full py-3 px-6 font-semibold rounded-lg transition-all duration-300 ${ isCurrent ? 'bg-gray-700 text-gray-400 cursor-not-allowed' : 'bg-amber-600 text-white hover:bg-amber-500 disabled:bg-gray-600' }`}>
          {loading ? 'Chargement...' : isCurrent ? 'Forfait Actuel' : 'Choisir ce forfait'}
        </button>
      </div>
    </motion.div>
  );
};


// --- COMPOSANT PRINCIPAL ---

const SubscriptionPlans = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [showPlans, setShowPlans] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null); // Pour la modale de paiement
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [showDetails, setShowDetails] = useState(false);
  const [renewalType, setRenewalType] = useState('automatic'); // 'automatic' or 'manual'
  
  const toggleDetails = () => setShowDetails(!showDetails);
  
  // Toggle between automatic and manual renewal
  const toggleRenewalType = () => {
    setRenewalType(prev => prev === 'automatic' ? 'manual' : 'automatic');
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!user || !user.id) { 
        console.log('Utilisateur non connecté ou ID manquant');
        setLoading(false); 
        setError("Utilisateur non connecté."); 
        return; 
      }
      
      try {
        setLoading(true);
        setError('');
        
        console.log(`Début du chargement des données pour l'utilisateur ${user.id}...`);
        
        // 1. Récupérer les données en parallèle
        const [subscriptionResponse, plansResponse, methodsResponse] = await Promise.allSettled([
          getSubscription(user.id),
          getSubscriptionPlans(),
          getPaymentMethods(user.id)
        ]);
        
        // 2. Traiter la réponse de l'abonnement
        let subscription = null;
        if (subscriptionResponse.status === 'fulfilled') {
          subscription = subscriptionResponse.value;
          console.log('Abonnement récupéré:', subscription);
        } else {
          console.error('Erreur lors de la récupération de l\'abonnement:', subscriptionResponse.reason);
        }
        
        // 3. Traiter la réponse des plans
        let plans = [];
        if (plansResponse.status === 'fulfilled') {
          plans = Array.isArray(plansResponse.value) ? plansResponse.value : [];
          console.log('Plans récupérés:', plans);
        } else {
          console.error('Erreur lors de la récupération des plans:', plansResponse.reason);
          setError('Impossible de charger les plans d\'abonnement');
        }
        
        // 4. Traiter la réponse des méthodes de paiement
        let methods = [];
        if (methodsResponse.status === 'fulfilled') {
          methods = Array.isArray(methodsResponse.value) ? methodsResponse.value : [];
          console.log('Méthodes de paiement récupérées:', methods);
        } else {
          console.error('Erreur lors de la récupération des méthodes de paiement:', methodsResponse.reason);
        }
        
        // 5. Mettre à jour l'état
        setCurrentSubscription(subscription);
        setSubscriptionPlans(plans);
        setPaymentMethods(methods);
        
        // 6. Sélectionner la première méthode de paiement disponible
        if (methods.length > 0) {
          setSelectedPaymentMethod(methods[0].id);
        }
        
        // 7. Afficher les plans si pas d'abonnement actif
        const hasActiveSubscription = subscription && subscription.status === 'active';
        setShowPlans(!hasActiveSubscription);
        
        console.log('État final:', { 
          hasSubscription: !!subscription, 
          subscriptionStatus: subscription?.status,
          plansCount: plans.length,
          paymentMethodsCount: methods.length
        });
        
      } catch (err) {
        console.error('Erreur inattendue lors du chargement des données:', err);
        setError('Une erreur est survenue lors du chargement des données.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [user]);

  // Étape 1: L'utilisateur clique sur "Choisir ce forfait"
  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
  };

  // Fonction pour charger les données d'abonnement
  const loadSubscriptionData = useCallback(async () => {
    if (!user?.id) return;
    
    setLoading(true);
    try {
      const subscription = await getSubscription(user.id);
      setCurrentSubscription(subscription || null);
      
      // Set renewal type based on subscription data if it exists
      if (subscription?.renewal_type) {
        setRenewalType(subscription.renewal_type);
      }
      
      // Charger les plans d'abonnement
      const plans = await getSubscriptionPlans();
      setSubscriptionPlans(plans);
    } catch (error) {
      console.error('Erreur lors du chargement des données d\'abonnement:', error);
      toast.error('Erreur lors du chargement des informations d\'abonnement');
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Charger les données au montage du composant
  useEffect(() => {
    loadSubscriptionData();
  }, [loadSubscriptionData]);

  useEffect(() => {
    const refreshPlans = async () => {
      try {
        const plans = await getSubscriptionPlans();
        if (Array.isArray(plans)) {
          setSubscriptionPlans(plans);
        }
      } catch (err) {
        console.warn('subscription_plans_updated refresh:', err);
      }
    };
    socketService.connect().catch(() => {});
    const unsub = socketService.on('subscription_plans_updated', refreshPlans);
    return () => {
      unsub?.();
    };
  }, []);
  
  // Étape 2: L'utilisateur confirme le paiement dans la modale
  const handleConfirmSubscription = async () => {
    if (!selectedPlan || !selectedPaymentMethod) {
      toast.error('Veuillez sélectionner un forfait et une méthode de paiement.');
      return;
    }
    
    setProcessing(true);
    try {
      console.log('Tentative de souscription avec:', {
        userId: user.id,
        planId: selectedPlan.id,
        paymentMethodId: selectedPaymentMethod,
        amount: selectedPlan.price,
        currency: 'EUR'
      });
      
      const result = await subscribeToPlan({ 
        userId: user.id, 
        planId: selectedPlan.id, 
        paymentMethodId: selectedPaymentMethod,
        amount: selectedPlan.price,
        currency: 'EUR',
        renewalType: renewalType // Include renewal type in the subscription
      });
      
      console.log('Résultat de la souscription:', result);
      
      if (result && result.success) {
        // Mise à jour de l'état avec le nouvel abonnement
        setCurrentSubscription(result.subscription || { plan_id: selectedPlan.id, status: 'active' });
        setShowPlans(false);
        setSelectedPlan(null);
        
        // Afficher un message de succès personnalisé si disponible
        toast.success(result.message || 'Abonnement souscrit avec succès !');
        
        // Rafraîchir les données de l'utilisateur et de l'abonnement
        if (user?.refreshUserData) {
          await user.refreshUserData();
        }
        
        // Recharger les données d'abonnement
        await loadSubscriptionData();
        
        // Si c'est un paiement traité, on peut forcer un rafraîchissement complet
        if (result.paymentProcessed) {
          // Attendre un court instant pour s'assurer que les données sont à jour
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        }
      } else {
        // Gérer le cas où la réponse n'est pas un succès
        const errorMessage = result?.message || 'La réponse du serveur est invalide';
        console.error('Réponse inattendue du serveur:', result);
        toast.error(errorMessage);
      }
    } catch (err) {
      console.error('Erreur lors de la souscription:', {
        error: err,
        response: err.response?.data,
        status: err.response?.status
      });
      
      // Gestion des erreurs spécifiques
      if (err.response?.data?.code === 'SUBSCRIPTION_ALREADY_ACTIVE' || 
          err.response?.data?.message?.includes('existe déjà')) {
        // Mettre à jour l'état pour refléter l'abonnement existant
        setCurrentSubscription({ plan_id: selectedPlan.id, status: 'active' });
        setShowPlans(false);
        setSelectedPlan(null);
        toast.success('Votre abonnement est déjà actif.');
      } else {
        // Afficher un message d'erreur générique pour les autres erreurs
        toast.error(err.message || 'Une erreur est survenue lors de la souscription. Veuillez réessayer.');
      }
    } finally {
      setProcessing(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        <header className="bg-gray-800/50 border border-amber-500/20 rounded-2xl p-8 text-center">
            <div className="inline-block p-4 bg-gray-900 rounded-full mb-4">
              <FiShield className="w-10 h-10 text-amber-400" />
            </div>
            <h1 className="text-3xl lg:text-4xl font-bold text-white">Gérer votre Abonnement</h1>
            <p className="text-gray-400 mt-2 max-w-2xl mx-auto">Choisissez le forfait qui vous correspond pour débloquer des fonctionnalités exclusives.</p>
        </header>

        {error && (
          <div className="bg-red-900/30 border border-red-700/50 text-red-300 px-6 py-4 rounded-xl flex items-center gap-3">
            <FiAlertCircle className="w-6 h-6" />
            <span>{error}</span>
          </div>
        )}

        {!showPlans && currentSubscription && (
          <SubscriptionStatus 
            subscription={currentSubscription} 
            onUpgrade={() => setShowPlans(true)}
            onViewDetails={toggleDetails}
          />
        )}

        {(showPlans || !currentSubscription) && (
          <div className="mt-8">
            <h2 className="text-2xl font-semibold text-center text-white mb-10">
              {currentSubscription ? 'Passez au forfait supérieur' : 'Choisissez votre forfait'}
            </h2>
            {console.log('Rendering plans:', { subscriptionPlans, currentSubscription, showPlans })}
            {subscriptionPlans.length === 0 ? (
              <div className="text-center py-10">
                <p className="text-gray-400">Aucun plan d'abonnement disponible pour le moment.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
                {subscriptionPlans.map((plan) => {
                  console.log('Rendering plan:', plan);
                  return (
                    <SubscriptionCard
                      key={plan.id}
                      plan={plan}
                      isCurrent={currentSubscription?.subscription_type === plan.id}
                      onSubscribe={handleSelectPlan}
                      loading={processing}
                    />
                  );
                })}
              </div>
            )}
            {currentSubscription && showPlans && (
              <div className="mt-8 text-center">
                <button onClick={() => setShowPlans(false)} disabled={processing} className="text-amber-400 hover:text-amber-300 font-medium">
                  Retour
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* --- MODALE DE DÉTAILS --- */}
      <AnimatePresence>
          {showDetails && currentSubscription && (() => {
            const startDate = new Date(currentSubscription.start_date);
            const endDate = new Date(currentSubscription.end_date);
            const totalDuration = (endDate - startDate);
            const elapsedDuration = (new Date() - startDate);
            const progress = totalDuration > 0 ? Math.min(100, (elapsedDuration / totalDuration) * 100) : 0;
            const daysRemaining = Math.max(0, Math.ceil((endDate - new Date()) / (1000 * 60 * 60 * 24)));
            
            return (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={toggleDetails}>
                <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
                  onClick={(e) => e.stopPropagation()} className="relative bg-gray-800 border border-slate-700 rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
                  <div className="p-6 border-b border-slate-700 flex justify-between items-center"><h2 className="text-2xl font-bold text-white">Détails de l'Abonnement</h2><button onClick={toggleDetails} className="text-gray-400 hover:text-white p-2 rounded-full"><FiX /></button></div>
                  <div className="p-6 space-y-6 overflow-y-auto">
                    <div className="bg-gradient-to-r from-amber-500/10 to-yellow-500/10 p-6 rounded-xl border border-amber-500/20"><div className="flex items-center justify-between"><div><p className="text-sm font-medium text-amber-400">VOTRE FORFAIT</p><h3 className="text-2xl font-bold text-white mt-1 capitalize">{currentSubscription.subscription_type}</h3></div><div className={`px-3 py-1 rounded-full text-sm font-medium bg-green-500/20 text-green-400`}>Actif</div></div><div className="mt-6"><div><div className="flex justify-between text-sm"><span className="font-medium text-gray-300">Progression</span><span className="font-medium text-amber-400">{daysRemaining} jours restants</span></div><div className="w-full bg-gray-700 rounded-full h-2.5 mt-2"><div className={`h-2.5 rounded-full ${progress > 80 ? 'bg-red-500' : progress > 50 ? 'bg-amber-500' : 'bg-green-500'}`} style={{ width: `${progress}%` }}></div></div></div></div></div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-gray-700/50 p-4 rounded-lg">
                        <div className="flex items-center gap-2 text-gray-300 mb-2">
                          <FiCalendar />
                          <h4>Période</h4>
                        </div>
                        <p className="font-medium">
                          {new Date(currentSubscription.start_date).toLocaleDateString('fr-FR')} → {new Date(currentSubscription.end_date).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                      <div className="bg-gray-700/50 p-4 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2 text-gray-300">
                            <FiClock />
                            <h4>Renouvellement</h4>
                          </div>
                          <button 
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleRenewalType();
                            }}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                              renewalType === 'automatic' ? 'bg-blue-500' : 'bg-gray-600'
                            }`}
                          >
                            <span className="sr-only">Changer le type de renouvellement</span>
                            <span 
                              className={`${
                                renewalType === 'automatic' ? 'translate-x-6' : 'translate-x-1'
                              } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                            />
                          </button>
                        </div>
                        <div className="flex items-center justify-between">
                          <p className="font-medium">
                            {renewalType === 'automatic' ? 'Automatique' : 'Manuel'}
                          </p>
                          <span className="text-xs text-gray-400">
                            {renewalType === 'automatic' 
                              ? 'Renouvellement automatique activé' 
                              : 'Renouvellement manuel activé'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="p-6 mt-auto border-t border-slate-700 flex justify-end"><button onClick={() => { toggleDetails(); setShowPlans(true); }} className="px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-500">Changer de Forfait</button></div>
                </motion.div>
              </motion.div>
            );
          })()}
      </AnimatePresence>

      {/* --- MODALE DE PAIEMENT --- */}
      <AnimatePresence>
        {selectedPlan && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => setSelectedPlan(null)}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()} className="relative bg-gray-800 border border-slate-700 rounded-2xl shadow-xl w-full max-w-md">
              <div className="p-6 border-b border-slate-700"><h2 className="text-2xl font-bold text-white">Confirmer l'Abonnement</h2></div>
              <div className="p-6 space-y-4">
                <p>Vous êtes sur le point de souscrire au forfait <strong className="text-amber-400">{selectedPlan.name}</strong> pour <strong className="text-white">{formatPlanPricePerMonth(selectedPlan.price, selectedPlan.currency, selectedPlan.billing_cycle)}</strong>.</p>
                <div>
                  <label className="text-sm font-medium text-gray-300">Méthode de paiement</label>
                  {paymentMethods.length > 0 ? (
                    <select value={selectedPaymentMethod} onChange={(e) => setSelectedPaymentMethod(e.target.value)} className="w-full mt-2 p-3 bg-gray-700 rounded-lg border border-gray-600">
                      {paymentMethods.map(method => <option key={method.id} value={method.id}>**** **** **** {method.last4}</option>)}
                    </select>
                  ) : (
                    <div className="mt-2 text-center p-4 border-2 border-dashed border-gray-600 rounded-lg">
                      <p className="text-gray-400">Aucune méthode de paiement.</p>
                      <button onClick={() => navigate('/demenageur/paiements')} className="mt-2 text-sm font-semibold text-amber-400 hover:underline">Ajouter une carte</button>
                    </div>
                  )}
                </div>
              </div>
              <div className="p-6 mt-auto border-t border-slate-700 flex justify-end gap-3">
                <button onClick={() => setSelectedPlan(null)} className="px-6 py-2 bg-gray-600 rounded-lg">Annuler</button>
                <button onClick={handleConfirmSubscription} disabled={processing || paymentMethods.length === 0} className="px-6 py-2 bg-green-600 text-white rounded-lg disabled:bg-gray-500">{processing ? 'Traitement...' : 'Confirmer et Payer'}</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SubscriptionPlans;